/**
 * Housing Unit Detail Footer DOM Component
 *
 * Renders the footer with pricing information and CTA buttons.
 * Displays a fixed footer pinned to the bottom of the detail sheet.
 *
 * @module nativeInteractiveSiteMap/components/housingUnitDetailFooter.dom
 */

import fastdom from 'fastdom';

/**
 * Renders the "Price" display block for housing units.
 * @param {Object} housingUnit
 * @returns {string} HTML string.
 */
function getPricingDisplay(housingUnit) {
  const displayPrice = housingUnit.price ? `$${housingUnit.price.toLocaleString()}` : null;

  return `
    <div class="nhs-u-display--flex nhs-u-items-center nhs-u-justify-content-space-between nhs-u-gap--200">
      <span class="nhs-mq-native-interactive-site-map__footer-label-large nhs-o-typography--sans-body-1-small-regular nhs-u-color--black-1000">
        Price
      </span>
      <span class="nhs-mq-native-interactive-site-map__footer-label-large nhs-o-typography--sans-body-1-medium-regular nhs-u-color--black-1000">
        ${displayPrice ?? ''}
      </span>
    </div>
  `;
}

/**
 * Renders the CTA buttons section. The "View more details" link is conditionally
 * rendered only if housingUnit.listingUrlPath is available — the backend (see
 * NativeInteractiveSiteMapService.GetLotMapInventoryAsync) already resolves it to an absolute
 * URL by prefixing Configuration.NewHomeSourceDomain, the same domain-source pattern used by
 * server-rendered cards (e.g. AppCode/Services/CommunityCardService.cs), so no client-side URL
 * building is needed here.
 * @param {Object} housingUnit
 * @returns {string} HTML string.
 */
function getCtaButtonsDisplay(housingUnit) {
  const detailUrl = housingUnit?.listingUrlPath;

  return `
    <div class="nhs-u-display--flex nhs-u-gap--500">
      ${detailUrl ? `
        <a class="nhs-c-button nhs-c-button--medium-40 nhs-c-button--primary-white nhs-u-flex--1 nhs-u-text--align-center"
          href="${detailUrl}" target="_blank" rel="noopener noreferrer">
          View more details
        </a>
      ` : ''}
      <button class="nhs-c-button nhs-c-button--medium-40 nhs-c-button--primary-lead nhs-u-flex--1" type="button">
        Contact builder
      </button>
    </div>
  `;
}

/**
 * Creates the Housing Unit Detail Footer component.
 * @param {Object} housingUnit - The currently selected housing unit with pricing
 * @returns {{ getElement: Function, remove: Function }}
 */
export default function createHousingUnitDetailFooterDom(housingUnit = null) {
  const state = { root: null };

  function initializeDom() {
    state.root = document.createElement('div');
    state.root.className = 'nhs-c-native-interactive-site-map__floorplan-detail-footer';

    if (!housingUnit) {
      return;
    }

    state.root.innerHTML = `
      <div class="nhs-u-flex-column nhs-u-gap--500 nhs-u-width--full">
        ${getPricingDisplay(housingUnit)}
        ${getCtaButtonsDisplay(housingUnit)}
      </div>
    `;
  }

  initializeDom();

  return {
    getElement() {
      return state.root;
    },

    remove() {
      fastdom.mutate(() => {
        state.root?.remove();
        state.root = null;
      });
    },
  };
}
